// Toolbar.jsx
// Standard ProseMirror toolbar with basic formatting commands

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Direct imports per bundle-barrel-imports rule (rule 2.1) — avoids loading all of @mui/material
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import RateReviewIcon from "@mui/icons-material/RateReview";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
    enableSuggestChanges,
    disableSuggestChanges,
    suggestChangesKey,
} from "@handlewithcare/prosemirror-suggest-changes";
import { authorHighlightKey, setAuthorHighlight } from "../../plugins/authorHighlightPlugin";
import { buildActions } from "./toolbarActions";
import { handleImageUpload } from "./toolbarUtils";
import TableMenu from "./TableMenu";

// Static dividers hoisted outside component to avoid re-creation on every render (rule 6.3)
const VERTICAL_DIVIDER = <Divider flexItem orientation="vertical" sx={{ height: 48 }} />;
const TOOLBAR_BOX_SX = {
    flexShrink: 0,
    zIndex: 10,
    bgcolor: 'white',
    borderBottom: '1px solid #CECFD2',
};
const HIDDEN_INPUT_STYLE = { display: "none" };

function Toolbar({ view, provider, collaboration = false, editorMode = 'full', currentUser = null }) {
    const [tick, setTick] = useState(0);
    const fileInputRef = useRef(null);
    const showSuggestionModeToggle = editorMode !== 'basic';

    // Re-render whenever the editor selection/doc changes
    useEffect(() => {
        if (!view) return;
        const prev = view.updateState;
        view.updateState = (state) => {
            prev.call(view, state);
            // Functional setState update (rule 5.11) — no stale closure risk
            setTick((t) => t + 1);
        };
        return () => {
            view.updateState = prev;
        };
    }, [view]);

    const actions = useMemo(() => {
        void tick;
        return buildActions(view, provider, fileInputRef, {collaboration, editorMode, currentUser});
    }, [view, provider, fileInputRef, tick, collaboration, editorMode, currentUser]);

    // Read current suggestion-mode state — simple boolean extraction, no useMemo needed (rule 5.3)
    const inSuggestionMode = view
        ? (suggestChangesKey.getState(view.state)?.enabled ?? false)
        : false;
    // Depend on tick so the value refreshes after each editor state update.
    void tick;

    // Read current author-highlight state — simple boolean, no useMemo needed (rule 5.3)
    const showAuthors = view
        ? (authorHighlightKey.getState(view.state)?.enabled ?? false)
        : false;

    // useCallback for stable handler references that don't change across renders (rule 8.3)
    const handleEnableEditing = useCallback(() => {
        if (!view) return;
        disableSuggestChanges(view.state, view.dispatch);
    }, [view]);

    const handleEnableSuggesting = useCallback(() => {
        if (!view) return;
        enableSuggestChanges(view.state, view.dispatch);
    }, [view]);

    const handleToggleAuthors = useCallback(() => {
        if (!view) return;
        setAuthorHighlight(view, !showAuthors);
    }, [view, showAuthors]);

    const handleFileChange = useCallback(() => {
        handleImageUpload(view, provider, fileInputRef);
    }, [view, provider]);

    return (
        <Box
            className="pm-toolbar"
            role="toolbar"
            aria-label="Editor toolbar"
            px={1}
            sx={TOOLBAR_BOX_SX}
        >
            {/* Hidden file input for image uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={HIDDEN_INPUT_STYLE}
                onChange={handleFileChange}
            />

            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} justifyContent={'space-between'}>

                <Stack direction={'row'} alignItems="center" gap={1}>
                    {actions.map((a) => {
                        if (a.separator) {
                            return <Divider key={a.key} flexItem orientation="vertical" sx={{ height: 48 }} />;
                        }

                        // Handle select dropdowns (e.g., heading selector)
                        if (a.type === "select") {
                            const currentValue = a.current();
                            return (
                                <Select
                                    key={a.key}
                                    variant="outlined"
                                    size='small'
                                    value={currentValue}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        a.run(value);
                                    }}
                                    sx={{ border: 'none' }}
                                    mx={0.5}
                                >
                                    {a.options.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            );
                        }

                        // Handle table menu dropdown
                        if (a.type === "table_menu") {
                            return <TableMenu key={a.key} action={a} />;
                        }

                        // Handle regular buttons
                        const enabled = a.enabled();
                        const active = a.active();
                        return (
                            <Tooltip key={a.key} title={a.label} arrow>
                                <span>
                                    <IconButton
                                        color={active ? "primary" : "default"}
                                        disabled={!enabled}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            a.run();
                                        }}
                                        sx={{
                                            bgcolor: active ? "action.selected" : "transparent",
                                        }}
                                    >
                                        {a.icon}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })}

                </Stack>

                <Stack direction={'row'} alignItems="center" gap={1}>
                    {showSuggestionModeToggle && (
                        <>
                            {VERTICAL_DIVIDER}

                            {/* Editing / Suggesting mode selector */}
                            <Tooltip title={inSuggestionMode ? "Suggesting mode — changes are tracked" : "Editing mode — changes are applied directly"} arrow>
                                <ButtonGroup
                                    size="small"
                                    variant="outlined"
                                    className="pm-suggestion-mode-toggle"
                                    sx={{ mr: 0.5 }}
                                >
                                    <Button
                                        startIcon={<EditIcon fontSize="small" />}
                                        onClick={handleEnableEditing}
                                        variant={inSuggestionMode ? "outlined" : "contained"}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            px: 1.5,
                                        }}
                                    >
                                        Editing
                                    </Button>
                                    <Button
                                        startIcon={<RateReviewIcon fontSize="small" />}
                                        onClick={handleEnableSuggesting}
                                        variant={inSuggestionMode ? "contained" : "outlined"}
                                        color={inSuggestionMode ? "success" : "primary"}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            px: 1.5,
                                        }}
                                    >
                                        Suggesting
                                    </Button>
                                </ButtonGroup>
                            </Tooltip>

                            {VERTICAL_DIVIDER}
                        </>
                    )}

                    {/* Show Authors toggle */}
                    <Tooltip title={showAuthors ? "Hide author highlights" : "Show who wrote what"} arrow>
                        <IconButton
                            size="small"
                            onClick={handleToggleAuthors}
                            color={showAuthors ? "primary" : "default"}
                            sx={{
                                bgcolor: showAuthors ? "action.selected" : "transparent",
                            }}
                        >
                            <PeopleAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                </Stack>
            </Stack>
        </Box>
    );
}

export default Toolbar;

